import { HostingEnvironment, type AppRegistryCallbackContext } from '@opengov/psp-contract';
import {
  MenuSectionTitle,
  MenuGroupName,
  EntityApplicationProductKey,
  AppCategory,
  StaticAppConfig,
  TestAppConfig,
  EntitledAppConfig,
  UnresolvedAppConfig,
  PspModuleConfigContext,
} from '../types';
import { FeatureFlagKey } from '../hooks/useFeatureFlags';
import { getBlueprintApiBaseUrl, resolveBlueprintModuleUrl } from './blueprintMfeResolver';
import {
  getHostingEnvironmentDomain,
  getPspOrigin,
  plcCommunitySlug,
  resolveDevModuleUrl,
  resolveProductTenantSlug,
} from './utils';
import { lazy } from 'react';
import { queryClient } from './queryClient';
import { adminSettingsModuleUrl } from './adminSettingsModule';
import { gabCoreModuleUrl } from './gabCoreModule';
import { gabV1Origin } from './gabDynamicAppConfigs';

/**
 * Fetches /admin/config from the given API base URL using TanStack Query.
 * Uses queryClient.fetchQuery for caching and deduplication of in-flight requests.
 */
async function fetchAdminConfig(
  apiBaseUrl: string,
  token?: string
): Promise<Record<string, unknown>> {
  return queryClient.fetchQuery({
    queryKey: ['adminConfig', apiBaseUrl],
    queryFn: async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/admin/config`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) {
          console.warn(
            `[adminConfig] Failed to fetch ${apiBaseUrl}/admin/config: ${response.status}`
          );
          return {};
        }

        return await response.json();
      } catch (err) {
        console.warn(`[adminConfig] Failed to fetch ${apiBaseUrl}/admin/config:`, err);
        return {};
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - admin config doesn't change frequently
  });
}

// IM module URL forwarded to EAM (which imports + mounts IM directly). Resolves
// like PSP's own Inventory loading — same resolveDevModuleUrl + dev override — so
// EAM and PSP always target the same IM.
const getInventoryModuleUrl = (ctx: AppRegistryCallbackContext): string =>
  ctx.hostingEnvironment === HostingEnvironment.DEVELOPMENT
    ? resolveDevModuleUrl('INVENTORY', 'http://localhost:4001/module/index.js')
    : `https://inventory.${getHostingEnvironmentDomain()}/module/index.js`;

// IM API origin = the module URL's origin (nginx co-hosts /module + /api).
const getInventoryApiOrigin = (ctx: AppRegistryCallbackContext): string =>
  new URL(getInventoryModuleUrl(ctx)).origin;

export const hasInventoryEntitlement = (ctx: PspModuleConfigContext): boolean =>
  ctx.entitledProductKeys?.includes(EntityApplicationProductKey.INVENTORY) ?? false;

// Gates the Fixed Asset Code Entry picker (in EAM) on the entity actually
// having the Fixed Assets entitlement, not just the eam-fa-integration flag + PSP hosting.
const hasFixedAssetEntitlement = (ctx: PspModuleConfigContext): boolean =>
  ctx.entitledProductKeys?.includes(EntityApplicationProductKey.FIXED_ASSETS) ?? false;

// FA module URL — used both to mount Fixed Assets and to derive the FA API origin
// forwarded to EAM, so EAM always calls the same FA that PSP loads (dev override included).
const getFixedAssetModuleUrl = (ctx: AppRegistryCallbackContext): string => {
  const developmentUrl = 'http://localhost:4000/module/index.js';
  const integrationUrl = 'https://fa.ogintegration.us/module/index.mjs';
  switch (ctx.hostingEnvironment) {
    case HostingEnvironment.DEVELOPMENT:
      return resolveDevModuleUrl('FIXED_ASSETS', developmentUrl);
    case HostingEnvironment.INTEGRATION:
      return integrationUrl;
    case HostingEnvironment.STAGING:
      return 'https://fa.ogstaging.us/module/index.mjs';
    case HostingEnvironment.PRODUCTION:
      return 'https://fa.opengov.com/module/index.mjs';
    default:
      console.error(`[FA] Unhandled hosting environment: ${ctx.hostingEnvironment}`);
      return integrationUrl;
  }
};

// FA API origin = the module URL's origin (FA co-hosts /module + /api).
const getFixedAssetApiOrigin = (ctx: AppRegistryCallbackContext): string =>
  new URL(getFixedAssetModuleUrl(ctx)).origin;

// FIN GL base URL forwarded to EAM, which adds the path it needs (e.g. /rest/api for the COA picker).
const getFinGlBaseUrl = (): string => `https://erp.fms.${getHostingEnvironmentDomain()}`;

const accountsPayableModuleUrl = (ctx: AppRegistryCallbackContext): string => {
  switch (ctx.hostingEnvironment) {
    case HostingEnvironment.INTEGRATION:
      return 'https://ap.fms.ogintegration.us/psp/accounts-payable.js';
    case HostingEnvironment.STAGING:
      return 'https://ap.fms.ogstaging.us/psp/accounts-payable.js';
    case HostingEnvironment.PRODUCTION:
      return 'https://ap.fms.opengov.com/psp/accounts-payable.js';
    default:
      return 'https://ap.fms.ogintegration.us/psp/accounts-payable.js';
  }
};
// Entity-level (tenant) check, backed by entity-service's app-switcher lookup
// via EntityContext's useEntityProductEntitlementsQuery — unlike
// hasInventoryEntitlement above, this is the same for every user at the
// entity.
export const hasEntityInventoryEntitlement = (ctx: PspModuleConfigContext): boolean =>
  ctx.entityEntitledProductKeys?.includes(EntityApplicationProductKey.INVENTORY) ?? false;

// Entity-level (tenant) Financials check, same source as hasEntityInventoryEntitlement.
// `financial` is the one key shared by every ERP FIN app (GL, AP, Bank Rec, Subledgers,
// Project Accounting), so this is true for an entity that owns any of them.
// Hides EAM's GL Integration UI only; EAM enforces access on its server.
export const hasEntityFinancialsEntitlement = (ctx: PspModuleConfigContext): boolean =>
  ctx.entityEntitledProductKeys?.includes(EntityApplicationProductKey.FINANCIALS) ?? false;

// Taskmaster + Platform origins forwarded to MFEs that embed
// `@opengov/taskmaster-actions` (EAM today; Sites/Blueprint for CMS page approval).
// Bare Taskmaster origin (no /api suffix) — the component appends its own API path.
// getHostingEnvironmentDomain() already maps DEVELOPMENT -> ogintegration.us, same
// as INTEGRATION, so no per-env branching (or dev-server override) is needed here.
const getTaskmasterOrigin = (): string => `https://taskmaster.${getHostingEnvironmentDomain()}`;

// Lazy-load components to prevent circular dependencies with PspContext
// This breaks the import chain: PspContext → appRegistry → components → PspContext
const CommandCenterRouter = lazy(() =>
  import('../pages/CommandCenterRouter').then((m) => ({ default: m.CommandCenterRouter }))
);
const AppRegistryEditorPage = lazy(() =>
  import('../pages/AppRegistryEditorPage').then((m) => ({ default: m.AppRegistryEditorPage }))
);
const NotificationsPage = lazy(() =>
  import('../pages/Notifications/NotificationsPage').then((m) => ({ default: m.NotificationsPage }))
);
const DevDataCreatorPage = lazy(() =>
  import('../pages/DevDataCreatorPage').then((m) => ({ default: m.DevDataCreatorPage }))
);

// Permitting & Licensing (product key `cit`). Mounted in-shell for dev + integration
// always, and on staging/production when the per-community `psp-enable-plc` flag is
// on; otherwise it tabs out to the legacy Ember app. (CIT-19220 / CIT-23005)
//
// Bundle URL: the plc-react CDN (CORS `*`). Each of the three entries below is a
// re-export pointer into an immutable per-build prefix, so a tab stays on one build
// even when the channel moves under it. They differ only in what moves the channel.
//
// Integration: advanced by every push to plc-ember-apps main.
const PLC_REACT_INTEGRATION_URL = 'https://react.vpctest.com/plc-react/main/render/PspEmployee.js';

// Staging: advanced deliberately, by dispatching plc-ember-apps'
// `react_staging_candidate.yml`. Staging must not track a channel that moves on its
// own, because it is where full regression runs — otherwise regression validates
// whatever main happened to build rather than a chosen build. Shares the dev CDN
// with integration for now; CIT-23520 moves it to staging-owned infrastructure so
// PSP loads the same artifact Ember-on-staging loads. (CIT-23479)
const PLC_REACT_STAGING_URL = 'https://react.vpctest.com/plc-react/candidate/render/PspEmployee.js';

// Prod: advanced by the release train, via the manifest PR in k8s-deployment-cit.
const PLC_REACT_PRODUCTION_URL =
  'https://react.opengov.com/plc-react/current/render/PspEmployee.js';

// Whether PLC mounts in-shell for the active LaunchDarkly context, evaluated at
// RUNTIME (per entity) — not the old build-time `import.meta.env.MODE` gate. Dev +
// integration always mount in-shell; staging + production mount only when the
// per-community `psp-enable-plc` flag is on, and otherwise tab out to the legacy
// Ember employee app. (CIT-23005)
const plcMountsInShell = (ctx: AppRegistryCallbackContext): boolean => {
  switch (ctx.hostingEnvironment) {
    case HostingEnvironment.DEVELOPMENT:
    case HostingEnvironment.INTEGRATION:
      return true;
    case HostingEnvironment.STAGING:
    case HostingEnvironment.PRODUCTION:
      return !!ctx.featureFlags?.[FeatureFlagKey.PSP_ENABLE_PLC];
    default:
      return false;
  }
};

// cit-components bundle URL for the current env (used only when mounting in-shell).
const plcModuleUrl = (ctx: AppRegistryCallbackContext): string => {
  switch (ctx.hostingEnvironment) {
    case HostingEnvironment.DEVELOPMENT:
      // Local plc-react dev server when VITE_DEV_MODULE_URL_PLC_EMPLOYEE is set,
      // otherwise the deployed integration bundle.
      return resolveDevModuleUrl('PLC_EMPLOYEE', PLC_REACT_INTEGRATION_URL);
    case HostingEnvironment.PRODUCTION:
      return PLC_REACT_PRODUCTION_URL;
    case HostingEnvironment.STAGING:
      return PLC_REACT_STAGING_URL;
    case HostingEnvironment.INTEGRATION:
    default:
      return PLC_REACT_INTEGRATION_URL;
  }
};

// Legacy Ember employee app URL for the current env — the tab-out target used when
// NOT mounting in-shell.
const plcLegacyEmberUrl = (ctx: AppRegistryCallbackContext): string => {
  // `{slug}.workflow.opengov.com` is the PLC community, published as
  // citCommunityId. The entity friendlyId is a different string when PLC was
  // provisioned on its own subdomain.
  const entitySlug = plcCommunitySlug(ctx.activeEntity);
  if (!entitySlug) {
    return '';
  }
  switch (ctx.hostingEnvironment) {
    case HostingEnvironment.DEVELOPMENT:
    case HostingEnvironment.INTEGRATION:
      return `https://${entitySlug}.stable.vpctest.io`;
    case HostingEnvironment.STAGING:
      return `https://${entitySlug}.vpcstaging.io`;
    case HostingEnvironment.PRODUCTION:
      return `https://${entitySlug}.workflow.opengov.com`;
    default:
      return '';
  }
};

// Permitting & Licensing (product key `cit`) — the single canonical PLC entry (one
// basePath, one product key). A single config expresses both topologies and picks
// at runtime via `plcMountsInShell(ctx)`:
//  - mount in-shell: `moduleRoutes` resolves to the cit-components bundle and
//    `externalUrl` resolves to '' — so the app is NOT treated as external/new-tab
//    (see `isNewTabApp`/`isIFrameApp`, which ignore an empty externalUrl).
//  - tab-out: `moduleRoutes` resolves to '' and `externalUrl` points at the legacy
//    Ember app; App.tsx skips the in-shell route for new-tab apps and the sidebar
//    opens the external URL in a new tab.
const permittingLicensingConfig: EntitledAppConfig = {
  id: EntityApplicationProductKey.PERMITTING_LICENSING,
  category: AppCategory.ENTITLED,
  basePath: '/permitting-licensing',
  displayName: 'Permitting & Licensing',
  triggerPageRefreshOnNavigateAway: (ctx: AppRegistryCallbackContext) =>
    !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
  moduleRoutes: [
    {
      pattern: '/',
      resolver: (ctx) => (plcMountsInShell(ctx) ? plcModuleUrl(ctx) : ''),
    },
  ],
  externalUrl: (ctx: AppRegistryCallbackContext) =>
    plcMountsInShell(ctx) ? '' : plcLegacyEmberUrl(ctx),
  openInNewTab: true,
  // TODO: Replace placeholder URN with real subject pattern once Permitting & Licensing adopts Beacon notifications
  resourceSubjectPatterns: ['urn:opengov:cit'],
  icon: 'PermittingLicensing',
  description: 'Build an experience applicants and staff love',
  menuSection: MenuSectionTitle.PRODUCTS,
};

// --- Products: Apps grouped by MenuSectionTitle.PRODUCTS, sorted alphabetically by displayName ---
/** Legis (OpenGov Legislative Management System) host per environment; module and API share it. */
const legisOrigin = (env: HostingEnvironment): string => {
  switch (env) {
    case HostingEnvironment.STAGING:
      return 'https://legislative.ogstaging.us';
    case HostingEnvironment.PRODUCTION:
      return 'https://legislative.opengov.com';
    default:
      return 'https://legislative.ogintegration.us';
  }
};

const products: readonly EntitledAppConfig[] = [
  {
    id: EntityApplicationProductKey.PRODUCT_311,
    category: AppCategory.ENTITLED,
    basePath: '/311',
    displayName: '311 Request Management',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    externalUrl: (ctx) => {
      // TODO: Derives the tenant host from friendlyId, so it breaks for any
      // entity whose 311 tenant slug diverges. Control Panel publishes no
      // `product311Id`; confirm with the 311 team whether citCommunityId covers
      // 311 (both are GAB-era products) or whether a new federated id is needed,
      // then adopt resolveProductTenantSlug().
      const entitySlug = ctx.activeEntity?.friendlyId;
      if (!entitySlug) return '';

      switch (ctx.hostingEnvironment) {
        case HostingEnvironment.DEVELOPMENT: {
          const devBase = import.meta.env.VITE_DEV_311_BASE;
          return devBase
            ? `https://${entitySlug}.${devBase}/`
            : `https://${entitySlug}.311-request-manager.ogintegration.us/`;
        }
        case HostingEnvironment.INTEGRATION:
          return `https://${entitySlug}.311-request-manager.ogintegration.us/`;
        case HostingEnvironment.STAGING:
          return `https://${entitySlug}.311-request-manager.ogstaging.us/`;
        case HostingEnvironment.PRODUCTION:
          return `https://${entitySlug}.311-request-manager.opengov.com/`;
        default:
          return `https://${entitySlug}.311-request-manager.ogintegration.us/`;
      }
    },
    openInNewTab: false,
    // 311 apps are built on GAB, so gab-core stamps their Beacon notifications with the GAB base
    // URN plus a `311` sub-namespace. This 3-segment pattern outranks App Builder's generic
    // `urn:opengov:gab` on specificity, so `urn:opengov:gab:311*` resolves here instead.
    resourceSubjectPatterns: ['urn:opengov:gab:311'],
    icon: 'Product311',
    description: 'Citizen service request management for government staff and residents',
    menuSection: MenuSectionTitle.PRODUCTS,
  },
  {
    id: EntityApplicationProductKey.ASSET_MANAGEMENT,
    category: AppCategory.ENTITLED,
    basePath: '/asset-management',
    displayName: 'Asset Management',
    // Always trigger page refresh on navigate away for Asset Management, regardless of feature flag
    triggerPageRefreshOnNavigateAway: () => true,
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          // Only the PRODUCTION branch below embeds this slug
          // (`{slug}.eam.opengov.com`). assetManagementId is the tenant subdomain.
          const entitySlug = resolveProductTenantSlug(ctx.activeEntity, 'assetManagementId');

          if (!entitySlug) {
            // Return empty string when entity not yet available - will retry on next render
            return '';
          }

          const eamEntryFile = 'eam-psp-entry.js';
          const devIntegrationUrl = `https://develop.eam.ogintegration.us/${eamEntryFile}`;

          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return resolveDevModuleUrl('ASSET_MANAGEMENT', devIntegrationUrl);
            case HostingEnvironment.INTEGRATION:
              return devIntegrationUrl;
            case HostingEnvironment.STAGING:
              console.error('Staging is currently not implemented for Asset Management.');
              return '';
            case HostingEnvironment.PRODUCTION:
              return `https://${entitySlug}.eam.opengov.com/${eamEntryFile}`;
            default:
              console.error(`Unhandled hosting environment: ${ctx.hostingEnvironment}`);
              return '';
          }
        },
      },
    ],
    moduleConfig: (ctx) => ({
      inventoryApiBaseUrl: getInventoryApiOrigin(ctx),
      inventoryModuleUrl: getInventoryModuleUrl(ctx),
      hasInventoryEntitlement: hasInventoryEntitlement(ctx),
      hasFixedAssetEntitlement: hasFixedAssetEntitlement(ctx),
      fixedAssetApiBaseUrl: getFixedAssetApiOrigin(ctx),
      hasEntityInventoryEntitlement: hasEntityInventoryEntitlement(ctx),
      hasEntityFinancialsEntitlement: hasEntityFinancialsEntitlement(ctx),
      finGlBaseUrl: getFinGlBaseUrl(),
      taskmasterBaseUrl: getTaskmasterOrigin(),
      pspBaseUrl: getPspOrigin(),
    }),
    // TODO: Replace placeholder URN with real subject pattern once Asset Management adopts Beacon notifications
    resourceSubjectPatterns: ['urn:opengov:eam'],
    icon: 'AssetManagement',
    description: 'Manage every asset lifecycle in one system',
    menuSection: MenuSectionTitle.PRODUCTS,
  },
  {
    id: EntityApplicationProductKey.FINANCIALS,
    category: AppCategory.ENTITLED,
    basePath: '/bankrec',
    displayName: 'Bank Reconciliation',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const integrationUrl = 'https://bankrec.fms.ogintegration.us/module/index.mjs';
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return resolveDevModuleUrl('BANK_RECONCILIATION', integrationUrl);
            case HostingEnvironment.INTEGRATION:
              return integrationUrl;
            case HostingEnvironment.STAGING:
              return 'https://bankrec.fms.ogstaging.us/module/index.mjs';
            case HostingEnvironment.PRODUCTION:
              return 'https://bankrec.fms.opengov.com/module/index.mjs';
            default:
              console.error(`[Bankrec] Unhandled hosting environment: ${ctx.hostingEnvironment}`);
              return integrationUrl;
          }
        },
      },
    ],
    moduleConfig: (ctx) => ({
      // Pass user info from PSP to bankrec
      userEmail: ctx.user?.email || '',
      userUuid: ctx.user?.sub || '',
      userName: ctx.user?.name || '',
    }),
    resourceSubjectPatterns: ['urn:opengov:financial:bank-reconciliation'],
    icon: 'BankReconciliation',
    description: 'Bank Reconciliation Application',
    menuSection: MenuSectionTitle.PRODUCTS,
    group: MenuGroupName.FINANCIALS,
  },
  {
    id: EntityApplicationProductKey.FIXED_ASSETS,
    category: AppCategory.ENTITLED,
    basePath: '/fixed-assets',
    displayName: 'Fixed Assets',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: getFixedAssetModuleUrl,
      },
    ],
    moduleConfig: (ctx) => ({
      userEmail: ctx.user?.email || '',
      userUuid: ctx.user?.sub || '',
      userName: ctx.user?.name || '',
    }),
    resourceSubjectPatterns: ['urn:opengov:financial:fixed-assets'],
    icon: 'Financials',
    description: 'Fixed Assets Application',
    menuSection: MenuSectionTitle.PRODUCTS,
    group: MenuGroupName.FINANCIALS,
  },
  {
    id: EntityApplicationProductKey.PCARD,
    category: AppCategory.ENTITLED,
    basePath: '/pcard',
    displayName: 'Purchase Cards',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const developmentUrl = 'http://localhost:4000/module/index.js';
          const integrationUrl = 'https://pcard.ogintegration.us/module/index.mjs';
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return resolveDevModuleUrl('PCARD', developmentUrl);
            case HostingEnvironment.INTEGRATION:
              return integrationUrl;
            case HostingEnvironment.STAGING:
              return 'https://pcard.ogstaging.us/module/index.mjs';
            case HostingEnvironment.PRODUCTION:
              return 'https://pcard.opengov.com/module/index.mjs';
            default:
              console.error(`[PCARD] Unhandled hosting environment: ${ctx.hostingEnvironment}`);
              return integrationUrl;
          }
        },
      },
    ],
    moduleConfig: (ctx) => ({
      userEmail: ctx.user?.email || '',
      userUuid: ctx.user?.sub || '',
      userName: ctx.user?.name || '',
    }),
    resourceSubjectPatterns: ['urn:opengov:financial:pcard'],
    icon: 'Financials',
    description: 'Purchase Cards Application',
    menuSection: MenuSectionTitle.PRODUCTS,
    group: MenuGroupName.FINANCIALS,
  },
  {
    id: EntityApplicationProductKey.INVENTORY,
    category: AppCategory.ENTITLED,
    basePath: '/inventory',
    displayName: 'Inventory',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const developmentUrl = 'http://localhost:4001/module/index.js';
          const integrationUrl = 'https://inventory.ogintegration.us/module/index.js';
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return resolveDevModuleUrl('INVENTORY', developmentUrl);
            case HostingEnvironment.INTEGRATION:
              return integrationUrl;
            case HostingEnvironment.STAGING:
              return 'https://inventory.ogstaging.us/module/index.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://inventory.opengov.com/module/index.js';
            default:
              console.error(`[Inventory] Unhandled hosting environment: ${ctx.hostingEnvironment}`);
              return integrationUrl;
          }
        },
      },
    ],
    moduleConfig: (ctx) => ({
      userEmail: ctx.user?.email || '',
      userUuid: ctx.user?.sub || '',
      userName: ctx.user?.name || '',
    }),
    resourceSubjectPatterns: ['urn:opengov:inventory'],
    icon: 'Financials',
    description: 'Inventory Management Application',
    menuSection: MenuSectionTitle.PRODUCTS,
  },
  {
    id: EntityApplicationProductKey.BUDGET,
    category: AppCategory.ENTITLED,
    basePath: '/budgets',
    displayName: 'Budgeting',
    featureFlagKey: FeatureFlagKey.PSP_ENABLE_BUDGETING,
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const integrationUrl = 'https://dy6148mdv4or.cloudfront.net/psp/budget-psp.js';
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return resolveDevModuleUrl('BUDGET', integrationUrl);
            case HostingEnvironment.INTEGRATION:
              return integrationUrl;
            case HostingEnvironment.STAGING:
              return 'https://d1gu8d8xe91oy6.cloudfront.net/psp/budget-psp.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://d32507ixvn160m.cloudfront.net/psp/budget-psp.js';
            default: {
              console.error(`Unhandled hosting environment: ${ctx.hostingEnvironment}`);
              return '';
            }
          }
        },
      },
    ],
    // TODO: Replace placeholder URN with real subject pattern once Budgeting adopts Beacon notifications
    resourceSubjectPatterns: ['urn:opengov:budget', 'urn:opengov:budgetRequests'],
    icon: 'BudgetingPerformance',
    description: 'Plan, track, and collaborate on budgets year-round',
    menuSection: MenuSectionTitle.PRODUCTS,
  },
  {
    id: EntityApplicationProductKey.COMMUNITY_FEEDBACK,
    category: AppCategory.ENTITLED,
    basePath: '/engagement',
    displayName: 'Community Engagement',
    featureFlagKey: FeatureFlagKey.PSP_ENABLE_COMMUNITY_FEEDBACK_2,
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const integrationUrl = 'https://d1s8zrq4hbgf3w.cloudfront.net/index.js';
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return resolveDevModuleUrl('COMMUNITY_FEEDBACK', integrationUrl);
            case HostingEnvironment.INTEGRATION:
              return integrationUrl;
            case HostingEnvironment.STAGING:
              return 'https://d3s29wrkctheuw.cloudfront.net/index.js';
            case HostingEnvironment.PRODUCTION:
              console.error(
                '[Community Feedback] Not implemented yet for this hosting environment.'
              );
              return '';
            default:
              console.error(
                `[Community Feedback] Unhandled hosting environment: ${ctx.hostingEnvironment}`
              );
              return integrationUrl;
          }
        },
      },
    ],
    // TODO: Replace placeholder URN with real subject pattern once Community Feedback adopts Beacon notifications
    resourceSubjectPatterns: ['urn:opengov:oth'],
    icon: 'CommunityFeedback',
    description: 'Collect feedback from surveys and meetings',
    menuSection: MenuSectionTitle.CAPABILITIES,
  },
  {
    id: EntityApplicationProductKey.FINANCIALS,
    category: AppCategory.ENTITLED,
    basePath: '/subsidiary-ledgers',
    featureFlagKey: FeatureFlagKey.PSP_UI_SUBSIDIARY_LEDGER,
    displayName: 'Financials',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    // TODO: Replace placeholder URN with real subject pattern once Subsidiary Ledgers adopts Beacon notifications
    resourceSubjectPatterns: ['urn:opengov:financial:subsidiary-ledgers'],
    icon: 'Financials',
    description: 'Legacy Financials Application',
    menuSection: MenuSectionTitle.PRODUCTS,
    group: MenuGroupName.FINANCIALS,
    externalUrl: (pspContext: AppRegistryCallbackContext) => {
      const entitySlug = resolveProductTenantSlug(pspContext.activeEntity, 'financialsCustomerId');
      if (!entitySlug) {
        return '';
      }
      const ogHostingDomain = getHostingEnvironmentDomain();
      return `https://${entitySlug}.fms.${ogHostingDomain}/`;
    },
  },
  {
    id: EntityApplicationProductKey.FINANCIALS,
    category: AppCategory.ENTITLED,
    basePath: '/accounts-payable',
    resourceSubjectPatterns: ['urn:opengov:financial:accounts-payable'],
    displayName: 'Accounts Payable',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [{ pattern: '/', resolver: accountsPayableModuleUrl }],
    unauthenticatedRoutes: [{ pattern: '/m/ocr-upload', resolver: accountsPayableModuleUrl }],
    icon: 'Financials',
    description: 'Accounts Payable',
    menuSection: MenuSectionTitle.PRODUCTS,
    group: MenuGroupName.FINANCIALS,
  },
  {
    id: EntityApplicationProductKey.FINANCIALS,
    category: AppCategory.ENTITLED,
    basePath: '/general-ledger',
    displayName: 'General Ledger',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const erpUrl = `https://erp.fms.${getHostingEnvironmentDomain()}/module/index.mjs`;
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              // For local GL development, set VITE_DEV_MODULE_URL_GENERAL_LEDGER
              // in .env.development.local to http://localhost:4002/module/index.mjs.
              // Run `nx run frontend:build` then `nx run frontend:prod-server` so
              // dist/module/index.mjs is served from that origin with CORS headers.
              return resolveDevModuleUrl('GENERAL_LEDGER', erpUrl);
            case HostingEnvironment.INTEGRATION:
              return erpUrl;
            case HostingEnvironment.STAGING:
              return erpUrl;
            case HostingEnvironment.PRODUCTION:
              return erpUrl;
            default:
              console.error(`[GL] Unhandled hosting environment: ${ctx.hostingEnvironment}`);
              return erpUrl;
          }
        },
      },
    ],
    moduleConfig: (ctx) => ({
      // Forwarded into ModuleMountOptions.registryConfig and read by our
      // psp-app.tsx → buildUserInfo → PSPContext → PSPAuthProvider. These
      // populate the mocked Auth0 user claims that drive useAuthDetails,
      // useAppQuery, etc.
      userEmail: ctx.user?.email || '',
      userUuid: ctx.user?.sub || '',
      userName: ctx.user?.name || '',
    }),
    resourceSubjectPatterns: ['urn:opengov:gl'],
    icon: 'GeneralLedger',
    description: 'General Ledger Application',
    menuSection: MenuSectionTitle.PRODUCTS,
    group: MenuGroupName.FINANCIALS,
  },
  {
    id: EntityApplicationProductKey.GRANTS_MANAGEMENT,
    category: AppCategory.ENTITLED,
    basePath: '/grants',
    displayName: 'Grants Management',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    externalUrl: (ctx) => {
      const entitySlug = ctx.activeEntity?.friendlyId;
      if (!entitySlug) return '';

      // TODO: `/org/{slug}/` is a path segment rather than a host, so a
      // mismatch yields a Grants-side 404 instead of an nginx one. Control Panel
      // publishes no Grants id; ask the GAB team which value keys their org
      // routes, then adopt resolveProductTenantSlug().
      if (ctx.hostingEnvironment === HostingEnvironment.DEVELOPMENT) {
        const devBase = import.meta.env.VITE_DEV_GRANTS_BASE;
        if (devBase) return `https://grants.${devBase}/org/${entitySlug}/`;
      }

      return `https://grants.${getHostingEnvironmentDomain()}/org/${entitySlug}/`;
    },
    openInNewTab: false,
    // Grants apps are built on GAB — same sub-namespace scheme as 311 above.
    resourceSubjectPatterns: ['urn:opengov:gab:grants'],
    icon: 'GrantsManagement',
    description: 'Grant lifecycle management for government staff',
    menuSection: MenuSectionTitle.PRODUCTS,
  },
  {
    id: EntityApplicationProductKey.HCM,
    category: AppCategory.ENTITLED,
    basePath: '/hcm',
    featureFlagKey: FeatureFlagKey.PSP_UI_CORE_HR,
    displayName: 'Human Capital Management',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              // Default to the integration host so non-HCM local development
              // gets a working sidebar entry. Override with a local Vite dev
              // server (e.g. `http://localhost:5173/psp/hcm-app.js`) when
              // working on HCM itself.
              return 'https://hcm.opengov.engineering/psp/hcm-app.js';
            case HostingEnvironment.INTEGRATION:
              return 'https://hcm.opengov.engineering/psp/hcm-app.js';
            case HostingEnvironment.STAGING:
              return 'https://hcm.ogstaging.us/psp/hcm-app.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://hcm.opengov.com/psp/hcm-app.js';
            default:
              console.error(`[HCM] Unhandled hosting environment: ${ctx.hostingEnvironment}`);
              return 'https://hcm.opengov.engineering/psp/hcm-app.js';
          }
        },
      },
    ],
    moduleConfig: (ctx) => ({
      // Forwarded as MountOptions.registryConfig into HCM's psp-entry, which
      // populates pspTokenStore.userInfo so AuthProvider/AuthSyncProvider see
      // the right user without re-decoding the JWT.
      userEmail: ctx.user?.email || '',
      userUuid: ctx.user?.sub || '',
      userName: ctx.user?.name || '',
    }),
    // TODO: Replace placeholder URN with real subject pattern once HCM adopts Beacon notifications
    resourceSubjectPatterns: ['urn:opengov:hcm'],
    icon: 'Hcm',
    description: 'Human Capital Management for HR, benefits, and workforce administration',
    menuSection: MenuSectionTitle.PRODUCTS,
    group: MenuGroupName.HUMAN_CAPITAL_MANAGEMENT,
  },
  {
    id: EntityApplicationProductKey.PAYROLL,
    category: AppCategory.ENTITLED,
    basePath: '/payroll',
    featureFlagKey: FeatureFlagKey.PSP_UI_PAYROLL,
    displayName: 'Payroll',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          // Override with a local Vite dev server when working on Payroll
          // itself: `http://localhost:5173/payroll-app.js`.
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return 'https://payroll.opengov.engineering/v2/psp/payroll-app.js';
            case HostingEnvironment.INTEGRATION:
              return 'https://payroll.opengov.engineering/v2/psp/payroll-app.js';
            case HostingEnvironment.STAGING:
              return 'https://payroll.ogstaging.us/v2/psp/payroll-app.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://payroll.opengov.com/v2/psp/payroll-app.js';
            default:
              console.error(`[Payroll] Unhandled hosting environment: ${ctx.hostingEnvironment}`);
              return 'https://payroll.opengov.engineering/v2/psp/payroll-app.js';
          }
        },
      },
    ],
    moduleConfig: (ctx) => ({
      // Forwarded as MountOptions.registryConfig into payroll-app's psp-entry.tsx,
      // which populates pspTokenStore so the in-app AuthProvider can read user
      // info without re-decoding the JWT.
      userEmail: ctx.user?.email || '',
      userUuid: ctx.user?.sub || '',
      userName: ctx.user?.name || '',
    }),
    // TODO: Replace placeholder URN with real subject pattern once Payroll adopts Beacon notifications
    resourceSubjectPatterns: ['urn:opengov:payroll'],
    icon: 'Payroll',
    description: 'Payroll processing, tax filings, and pay history',
    menuSection: MenuSectionTitle.PRODUCTS,
    group: MenuGroupName.HUMAN_CAPITAL_MANAGEMENT,
  },
  {
    id: EntityApplicationProductKey.WORKFORCE_PLANNING,
    category: AppCategory.ENTITLED,
    basePath: '/workforce-planning',
    featureFlagKey: FeatureFlagKey.PSP_UI_WORKFORCE_PLANNING,
    displayName: 'Workforce Planning',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return 'https://wfp.ogintegration.us/psp/wfp-app.js';
            case HostingEnvironment.INTEGRATION:
              return 'https://wfp.ogintegration.us/psp/wfp-app.js';
            case HostingEnvironment.STAGING:
              return 'https://wfp.ogstaging.us/psp/wfp-app.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://wfp.opengov.com/psp/wfp-app.js';
            default:
              console.error(`[WFP] Unhandled hosting environment: ${ctx.hostingEnvironment}`);
              return 'https://wfp.ogintegration.us/psp/wfp-app.js';
          }
        },
      },
    ],
    moduleConfig: (ctx) => ({
      userEmail: ctx.user?.email || '',
      userUuid: ctx.user?.sub || '',
      userName: ctx.user?.name || '',
    }),
    resourceSubjectPatterns: ['urn:opengov:wfp'],
    icon: 'WorkforcePlanning',
    description: 'Workforce planning, succession, and analytics',
    menuSection: MenuSectionTitle.PRODUCTS,
    group: MenuGroupName.HUMAN_CAPITAL_MANAGEMENT,
  },
  {
    id: EntityApplicationProductKey.LMS,
    category: AppCategory.ENTITLED,
    basePath: '/lms',
    featureFlagKey: FeatureFlagKey.PSP_UI_LMS,
    displayName: 'LMS',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return 'https://lms.ogintegration.us/psp/lms-app.js';
            case HostingEnvironment.INTEGRATION:
              return 'https://lms.ogintegration.us/psp/lms-app.js';
            case HostingEnvironment.STAGING:
              return 'https://lms.ogstaging.us/psp/lms-app.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://lms.opengov.com/psp/lms-app.js';
            default:
              console.error(`[LMS] Unhandled hosting environment: ${ctx.hostingEnvironment}`);
              return 'https://lms.ogintegration.us/psp/lms-app.js';
          }
        },
      },
    ],
    moduleConfig: (ctx) => ({
      userEmail: ctx.user?.email || '',
      userUuid: ctx.user?.sub || '',
      userName: ctx.user?.name || '',
    }),
    resourceSubjectPatterns: ['urn:opengov:lms'],
    icon: 'Hcm',
    description: 'Learning Management System',
    menuSection: MenuSectionTitle.PRODUCTS,
    group: MenuGroupName.HUMAN_CAPITAL_MANAGEMENT,
  },
  {
    id: EntityApplicationProductKey.FINANCIALS,
    category: AppCategory.ENTITLED,
    basePath: '/project-accounting',
    resourceSubjectPatterns: ['urn:opengov:financial:project-accounting'],
    displayName: 'Project Accounting',
    featureFlagKey: FeatureFlagKey.PSP_UI_PROJECT_ACCOUNTING,
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.INTEGRATION:
              return 'https://pa.fms.opengov.engineering/psp/project-accounting.js';
            case HostingEnvironment.STAGING:
              return 'https://pa.fms.ogstaging.us/psp/project-accounting.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://pa.fms.opengov.com/psp/project-accounting.js';
            default:
              return 'https://pa.fms.opengov.engineering/psp/project-accounting.js';
          }
        },
      },
    ],
    icon: 'Financials',
    description: 'Project Accounting',
    menuSection: MenuSectionTitle.PRODUCTS,
    group: MenuGroupName.FINANCIALS,
  },
  permittingLicensingConfig,
  {
    id: EntityApplicationProductKey.PROCUREMENT,
    category: AppCategory.ENTITLED,
    basePath: '/procurement',
    displayName: 'Procurement & Contract Management',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    resourceSubjectPatterns: ['urn:opengov:procurement'],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const integrationUrl = 'https://procurement.ogintegration.us/psp/index.js';
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return resolveDevModuleUrl('PROCUREMENT', integrationUrl);
            case HostingEnvironment.INTEGRATION:
              return integrationUrl;
            case HostingEnvironment.STAGING:
              return 'https://procurement.ogstaging.us/psp/index.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://procurement.opengov.com/psp/index.js';
            default:
              return integrationUrl;
          }
        },
      },
    ],
    icon: 'Procurement',
    description: 'Automate procurement from solicitation through contract',
    menuSection: MenuSectionTitle.PRODUCTS,
  },
  {
    id: EntityApplicationProductKey.REPORTING,
    category: AppCategory.ENTITLED,
    basePath: '/reporting',
    displayName: 'Reporting',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const integrationUrl = 'https://du0g9flf2mwtz.cloudfront.net/psp/reporting-psp.js';
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return resolveDevModuleUrl('REPORTING', integrationUrl);
            case HostingEnvironment.INTEGRATION:
              return integrationUrl;
            case HostingEnvironment.STAGING:
              return 'https://d37iau2st31ia1.cloudfront.net/psp/reporting-psp.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://d1y2nphsc694z0.cloudfront.net/psp/reporting-psp.js';
            default: {
              console.error(`Unhandled hosting environment: ${ctx.hostingEnvironment}`);
              return '';
            }
          }
        },
      },
    ],
    // TODO: Replace placeholder URN with real subject pattern once Reporting adopts Beacon notifications
    resourceSubjectPatterns: ['urn:opengov:reporting'],
    icon: 'Reporting',
    description: 'Analyze and share data and insights',
    menuSection: MenuSectionTitle.CAPABILITIES,
  },
  {
    id: EntityApplicationProductKey.PERFORMANCE_MANAGEMENT,
    category: AppCategory.ENTITLED,
    basePath: '/budgeting',
    displayName: 'Performance Management',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const entityIdentifier = ctx.activeEntity?.friendlyId;
          if (!entityIdentifier) {
            return '';
          }
          const integrationUrl = 'https://dhedrwbe8cd4c.cloudfront.net/assets/psp/budgeting-psp.js';
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return resolveDevModuleUrl('PERFORMANCE_MANAGEMENT', integrationUrl);
            case HostingEnvironment.INTEGRATION:
              return integrationUrl;
            case HostingEnvironment.STAGING:
              return 'https://d2a77ucn5akzna.cloudfront.net/assets/psp/budgeting-psp.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://d1uja9twkk93gj.cloudfront.net/assets/psp/budgeting-psp.js';
            default: {
              console.error(`Unhandled hosting environment: ${ctx.hostingEnvironment}`);
              return '';
            }
          }
        },
      },
    ],
    // TODO: Replace placeholder URN with real subject pattern once Performance Management adopts Beacon notifications
    resourceSubjectPatterns: ['urn:opengov:perfmgmt'],
    icon: 'PerformanceManagement',
    description: 'Measure and report your operational performance',
    menuSection: MenuSectionTitle.PRODUCTS,
  },
  {
    id: EntityApplicationProductKey.TAX_REVENUE,
    category: AppCategory.ENTITLED,
    basePath: '/tax-revenue',
    displayName: 'Tax & Revenue',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    externalUrl: (pspContext: AppRegistryCallbackContext) => {
      const entitySlug = resolveProductTenantSlug(pspContext.activeEntity, 'tnrCustomerId');
      if (!entitySlug) {
        return '';
      }
      const ogHostingDomain = getHostingEnvironmentDomain();
      return `https://${entitySlug}.taxandrevenue.${ogHostingDomain}/PSP/IframeLanding`;
    },
    // TODO: Replace placeholder URN with real subject pattern once Tax & Revenue adopts Beacon notifications
    resourceSubjectPatterns: ['urn:opengov:tnr'],
    icon: 'TaxRevenue',
    description: 'Manage tax and revenue in one place',
    menuSection: MenuSectionTitle.PRODUCTS,
  },
  {
    id: EntityApplicationProductKey.UTILITY_BILLING,
    category: AppCategory.ENTITLED,
    basePath: '/utility-billing',
    displayName: 'Utility Billing',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleConfig: async (pspContext: AppRegistryCallbackContext) => {
      // TODO: Builds a tenant host from friendlyId. Control Panel publishes no
      // id for the UB/AR/CR platform, so resolveProductTenantSlug() has nothing
      // to key on yet — ask that team whether these share the Financials tenant
      // (financialsCustomerId) or need a new federated id.
      const entitySlug = pspContext.activeEntity?.friendlyId;
      if (!entitySlug) {
        return {};
      }

      const getDomain = () => {
        switch (pspContext.hostingEnvironment) {
          case HostingEnvironment.DEVELOPMENT:
            return 'ub.opengov.engineering';
          case HostingEnvironment.INTEGRATION:
            return 'ub.opengov.engineering';
          case HostingEnvironment.STAGING:
            return 'ub.ogstaging.us';
          case HostingEnvironment.PRODUCTION:
            return 'ub.opengov.com';
          default:
            return 'ub.opengov.engineering';
        }
      };

      const apiBaseUrl = `https://${entitySlug}.${getDomain()}`;
      const token = pspContext.auth0Config?.token;
      const appConfig = await fetchAdminConfig(apiBaseUrl, token);

      return {
        apiBaseUrl,
        config: appConfig,
      };
    },
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const entitySlug = ctx.activeEntity?.friendlyId;
          if (!entitySlug) {
            // Return empty string when entity not yet available - will retry on next render
            return '';
          }

          const getDomain = () => {
            switch (ctx.hostingEnvironment) {
              case HostingEnvironment.DEVELOPMENT:
                return 'ub.opengov.engineering';
              case HostingEnvironment.INTEGRATION:
                return 'ub.opengov.engineering';
              case HostingEnvironment.STAGING:
                return 'ub.ogstaging.us';
              case HostingEnvironment.PRODUCTION:
                return 'ub.opengov.com';
              default:
                return 'ub.opengov.engineering';
            }
          };

          return `https://${entitySlug}.${getDomain()}/modules/ub/ub-admin.js`;
        },
      },
    ],
    // TODO: Replace placeholder URN with real subject pattern once Utility Billing adopts Beacon notifications
    resourceSubjectPatterns: ['urn:opengov:ub'],
    icon: 'UtilityBilling',
    description: 'Deliver seamless utility billing experiences',
    menuSection: MenuSectionTitle.PRODUCTS,
  },
  {
    // Gated on its own `ar` entitlement: Accounts Receivable is sold standalone,
    // so it must not require Financials or Utility Billing. The UB backend serves
    // the bundle but gates `/modules/ar` on the AR entitlement independently.
    id: EntityApplicationProductKey.ACCOUNTS_RECEIVABLE,
    category: AppCategory.ENTITLED,
    basePath: '/accounts-receivable',
    displayName: 'Accounts Receivable',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    icon: 'Financials',
    menuSection: MenuSectionTitle.PRODUCTS,
    group: MenuGroupName.FINANCIALS,
    // TODO: Replace placeholder URN with real subject pattern once Accounts Receivable adopts Beacon notifications
    resourceSubjectPatterns: ['urn:opengov:ar'],
    description: 'Accounts Receivable',
    // IMPORTANT: This tells AR where the UB backend is located
    moduleConfig: async (pspContext: AppRegistryCallbackContext) => {
      // TODO: Builds a tenant host from friendlyId. Control Panel publishes no
      // id for the UB/AR/CR platform, so resolveProductTenantSlug() has nothing
      // to key on yet — ask that team whether these share the Financials tenant
      // (financialsCustomerId) or need a new federated id.
      const entitySlug = pspContext.activeEntity?.friendlyId;
      if (!entitySlug) {
        return {};
      }

      const getDomain = () => {
        switch (pspContext.hostingEnvironment) {
          case HostingEnvironment.DEVELOPMENT:
            return 'ub.opengov.engineering';
          case HostingEnvironment.INTEGRATION:
            return 'ub.opengov.engineering';
          case HostingEnvironment.STAGING:
            return 'ub.ogstaging.us';
          case HostingEnvironment.PRODUCTION:
            return 'ub.opengov.com';
          default:
            return 'ub.opengov.engineering';
        }
      };

      const apiBaseUrl = `https://${entitySlug}.${getDomain()}`;
      const revenueCoreApiUrl = `http://revenue-api.${getDomain()}`;
      const ubBaseUrl = `/utility-billing`;
      const crBaseUrl = `/cash-receipts`;

      const token = pspContext.auth0Config?.token;
      const appConfig = await fetchAdminConfig(apiBaseUrl, token);

      return {
        graphqlApiUrl: apiBaseUrl,
        revenueCoreApiUrl: revenueCoreApiUrl,
        ubBaseUrl: ubBaseUrl,
        crBaseUrl: crBaseUrl,
        config: appConfig,
      };
    },
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const entitySlug = ctx.activeEntity?.friendlyId;
          if (!entitySlug) {
            // Return empty string when entity not yet available - will retry on next render
            return '';
          }

          const getDomain = () => {
            switch (ctx.hostingEnvironment) {
              case HostingEnvironment.DEVELOPMENT:
                return 'ub.opengov.engineering';
              case HostingEnvironment.INTEGRATION:
                return 'ub.opengov.engineering';
              case HostingEnvironment.STAGING:
                return 'ub.ogstaging.us';
              case HostingEnvironment.PRODUCTION:
                return 'ub.opengov.com';
              default:
                return 'ub.opengov.engineering';
            }
          };

          return `https://${entitySlug}.${getDomain()}/modules/ar/index.js`;
        },
      },
    ],
  },
  {
    // Gated on its own `cr` entitlement. Cash Receipts ships bundled with Utility
    // Billing but is also sold standalone, so it must not require `ub`: a UB
    // customer receives `cr` as part of the UB product bundle instead.
    id: EntityApplicationProductKey.CASH_RECEIPTS,
    category: AppCategory.ENTITLED,
    basePath: '/cash-receipts',
    displayName: 'Cash Receipts',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    icon: 'Financials',
    menuSection: MenuSectionTitle.PRODUCTS,
    group: MenuGroupName.FINANCIALS,
    // TODO: Replace placeholder URN with real subject pattern once Accounts Receivable adopts Beacon notifications
    resourceSubjectPatterns: ['urn:opengov:cr'],
    description: 'Cash Receipts',
    // IMPORTANT: This tells CR where the UB backend is located
    moduleConfig: async (pspContext: AppRegistryCallbackContext) => {
      // TODO: Builds a tenant host from friendlyId. Control Panel publishes no
      // id for the UB/AR/CR platform, so resolveProductTenantSlug() has nothing
      // to key on yet — ask that team whether these share the Financials tenant
      // (financialsCustomerId) or need a new federated id.
      const entitySlug = pspContext.activeEntity?.friendlyId;
      if (!entitySlug) {
        return {};
      }

      const getDomain = () => {
        switch (pspContext.hostingEnvironment) {
          case HostingEnvironment.DEVELOPMENT:
            return 'ub.opengov.engineering';
          case HostingEnvironment.INTEGRATION:
            return 'ub.opengov.engineering';
          case HostingEnvironment.STAGING:
            return 'ub.ogstaging.us';
          case HostingEnvironment.PRODUCTION:
            return 'ub.opengov.com';
          default:
            return 'ub.opengov.engineering';
        }
      };

      const apiBaseUrl = `https://${entitySlug}.${getDomain()}`;
      const revenueCoreApiUrl = `http://revenue-api.${getDomain()}`;
      const ubBaseUrl = `/utility-billing`;
      const crBaseUrl = `/cash-receipts`;

      const token = pspContext.auth0Config?.token;
      const appConfig = await fetchAdminConfig(apiBaseUrl, token);

      return {
        graphqlApiUrl: apiBaseUrl,
        revenueCoreApiUrl: revenueCoreApiUrl,
        ubBaseUrl: ubBaseUrl,
        crBaseUrl: crBaseUrl,
        config: appConfig,
      };
    },
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const entitySlug = ctx.activeEntity?.friendlyId;
          if (!entitySlug) {
            // Return empty string when entity not yet available - will retry on next render
            return '';
          }

          const getDomain = () => {
            switch (ctx.hostingEnvironment) {
              case HostingEnvironment.DEVELOPMENT:
                return 'ub.opengov.engineering';
              case HostingEnvironment.INTEGRATION:
                return 'ub.opengov.engineering';
              case HostingEnvironment.STAGING:
                return 'ub.ogstaging.us';
              case HostingEnvironment.PRODUCTION:
                return 'ub.opengov.com';
              default:
                return 'ub.opengov.engineering';
            }
          };

          return `https://${entitySlug}.${getDomain()}/modules/cr/cr-admin.js`;
        },
      },
    ],
  },
  {
    id: EntityApplicationProductKey.LEGIS,
    category: AppCategory.ENTITLED,
    basePath: '/legislative',
    displayName: 'Legis',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const integrationUrl = 'https://legislative.ogintegration.us/index.js';
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return resolveDevModuleUrl('LEGIS', integrationUrl);
            case HostingEnvironment.INTEGRATION:
              return integrationUrl;
            case HostingEnvironment.STAGING:
              return 'https://legislative.ogstaging.us/index.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://legislative.opengov.com/index.js';
            default:
              return integrationUrl;
          }
        },
      },
    ],
    // Legis reads these as MountOptions.registryConfig. Its API is on the module's own host,
    // because inside PSP the page origin is the shell's.
    moduleConfig: (ctx) => {
      const origin = legisOrigin(ctx.hostingEnvironment);
      return { apiBaseUrl: origin, tutorialsBaseUrl: `${origin}/tutorials` };
    },
    resourceSubjectPatterns: ['urn:opengov:legis'],
    icon: 'Legis',
    description:
      'Agendas, meetings, minutes, legislation and public records for councils, boards and commissions',
    menuSection: MenuSectionTitle.PRODUCTS,
  },
] as const satisfies readonly EntitledAppConfig[];

// --- Developer Tools: Apps grouped by MenuSectionTitle.DEVELOPER_TOOLS, sorted alphabetically by displayName ---
const devTools: readonly StaticAppConfig[] = [
  {
    id: 'app-registry-editor',
    category: AppCategory.STATIC,
    basePath: '/developer-app-registry-editor',
    displayName: 'App Registry Editor',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    component: AppRegistryEditorPage,
    icon: 'AppRegistryEditor',
    description: 'Developer tool to edit app registry entries',
    menuSection: MenuSectionTitle.DEVELOPER_TOOLS,
  },
  {
    id: 'dev-data-creator',
    category: AppCategory.STATIC,
    basePath: '/developer-data-creator',
    displayName: 'Data Creator',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    component: DevDataCreatorPage,
    icon: 'Wrench',
    description: 'Create tasks and notifications for testing',
    menuSection: MenuSectionTitle.DEVELOPER_TOOLS,
  },
] as const satisfies readonly StaticAppConfig[];

// --- Action Hubs: Apps grouped by MenuSectionTitle.ACTION_HUBS, sorted alphabetically by displayName ---
const actionHubs: readonly (StaticAppConfig | EntitledAppConfig)[] = [
  {
    id: 'command-center',
    category: AppCategory.STATIC,
    basePath: '/',
    displayName: 'Command Center',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    component: CommandCenterRouter,
    icon: 'CommandCenter',
    description: 'Main dashboard and command center',
    menuSection: MenuSectionTitle.ACTION_HUBS,
  },
] as const satisfies readonly (StaticAppConfig | EntitledAppConfig)[];

// Action Hub app gated by LaunchDarkly flag.
// Lives outside `actionHubs` because that array rejects TestAppConfig at the type level.
const pspDemoAssistantConfig: TestAppConfig = {
  id: 'psp-demo-assistant',
  category: AppCategory.TEST,
  basePath: '/psp-demo-assistant',
  displayName: 'PSP Demo Assistant',
  triggerPageRefreshOnNavigateAway: (ctx) =>
    !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
  icon: 'AgentStudio',
  description: 'Experimental Demo Assistant',
  menuSection: MenuSectionTitle.ACTION_HUBS,
  featureFlagKey: FeatureFlagKey.PSP_DEMO_ASSISTANT_APP,
  moduleRoutes: [
    {
      pattern: '/',
      resolver: (ctx) => {
        switch (ctx.hostingEnvironment) {
          case HostingEnvironment.DEVELOPMENT:
            return 'https://psp-demo-assistant.ogintegration.us/module/index.js';
          case HostingEnvironment.INTEGRATION:
            return 'https://psp-demo-assistant.ogintegration.us/module/index.js';
          case HostingEnvironment.STAGING:
            return 'https://psp-demo-assistant.ogstaging.us/module/index.js';
          case HostingEnvironment.PRODUCTION:
            return 'https://psp-demo-assistant.opengov.com/module/index.js';
          default:
            return 'https://psp-demo-assistant.ogintegration.us/module/index.js';
        }
      },
    },
  ],
};

// Government OS (OpenGov/product-engineering-labs, opengov-assist/apps/sandbox): the
// AI-native government workspace, served as a remote module. Shaped like the demo
// assistant above: TEST category, so the feature flag alone decides visibility and no
// entitlement is required, and registered unconditionally (not in the dev-only testApps).
// Only integration is deployed; the staging/production hosts follow the house
// convention and stay behind the flag, which is off by default.
const governmentOsConfig: TestAppConfig = {
  id: 'government-os',
  category: AppCategory.TEST,
  basePath: '/government-os',
  displayName: 'Government OS',
  triggerPageRefreshOnNavigateAway: (ctx) =>
    !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
  icon: 'AgentStudio',
  description: 'AI-native government workspace',
  menuSection: MenuSectionTitle.ACTION_HUBS,
  featureFlagKey: FeatureFlagKey.PSP_UI_GOVERNMENT_OS,
  moduleRoutes: [
    {
      pattern: '/',
      resolver: (ctx) => {
        switch (ctx.hostingEnvironment) {
          case HostingEnvironment.DEVELOPMENT:
            return 'https://government-os.ogintegration.us/module/index.js';
          case HostingEnvironment.INTEGRATION:
            return 'https://government-os.ogintegration.us/module/index.js';
          case HostingEnvironment.STAGING:
            return 'https://government-os.ogstaging.us/module/index.js';
          case HostingEnvironment.PRODUCTION:
            return 'https://government-os.opengov.com/module/index.js';
          default:
            return 'https://government-os.ogintegration.us/module/index.js';
        }
      },
    },
  ],
};

// OG Intelligence: the TrustGraph workbench, embedded in an iframe and gated by the
// same flag as Government OS.
//
// The workbench sits behind an ALB that signs in through Auth0, on the same tenant PSP
// uses (login.ogintegration.us). Auth0's login page sends `frame-ancestors 'none'`, so
// it can never render in this iframe. A user already signed in to PSP never sees it:
// Auth0 redirects straight back and the frame loads. psp, login and intelligence-labs
// are all on ogintegration.us, so the ALB's cookies are same-site and are sent in the
// frame.
//
// Only integration hosts a workbench. Other environments resolve to '' (not an iframe
// app) and the flag stays off there.
const ogIntelligenceConfig: TestAppConfig = {
  id: 'og-intelligence',
  category: AppCategory.TEST,
  basePath: '/og-intelligence',
  displayName: 'OG Intelligence',
  triggerPageRefreshOnNavigateAway: (ctx) =>
    !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
  icon: 'DataManager',
  description: 'Knowledge graph workbench',
  menuSection: MenuSectionTitle.PRODUCTS,
  featureFlagKey: FeatureFlagKey.PSP_UI_GOVERNMENT_OS,
  externalUrl: (ctx) => {
    switch (ctx.hostingEnvironment) {
      case HostingEnvironment.DEVELOPMENT:
      case HostingEnvironment.INTEGRATION:
        return 'https://intelligence-labs.ogintegration.us/';
      default:
        return '';
    }
  },
  openInNewTab: false,
};

// OG360 (OpenGov/og360-web): OpenGov's internal AI-native cockpit for go-to-market teams,
// served as a remote module. ENTITLED on the `og360` product key, so it shows only in entities
// where the user holds the OG360 entitlement (Control Panel), and no feature flag is needed.
// Visibility is not authorization: OG360's data reads are authorized server-side by gab-core
// (an OG360 role on the OG360 tenant).
const og360Config: EntitledAppConfig = {
  id: EntityApplicationProductKey.OG360,
  category: AppCategory.ENTITLED,
  basePath: '/og360',
  displayName: 'OG360',
  triggerPageRefreshOnNavigateAway: (ctx) =>
    !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
  icon: 'Og360',
  description: 'AI-native cockpit for OpenGov go-to-market teams',
  menuSection: MenuSectionTitle.ACTION_HUBS,
  moduleRoutes: [
    {
      pattern: '/',
      resolver: (ctx) => {
        const integrationUrl = 'https://og360.ogintegration.us/module/index.js';
        switch (ctx.hostingEnvironment) {
          case HostingEnvironment.DEVELOPMENT:
            return resolveDevModuleUrl('OG360', integrationUrl);
          case HostingEnvironment.INTEGRATION:
            return integrationUrl;
          case HostingEnvironment.STAGING:
            return 'https://og360.ogstaging.us/module/index.js';
          case HostingEnvironment.PRODUCTION:
            return 'https://og360.opengov.com/module/index.js';
          default:
            return integrationUrl;
        }
      },
    },
  ],
};

// --- Test Apps: Apps grouped by MenuSectionTitle.TEST_APPS, sorted alphabetically by displayName ---
const testApps: readonly TestAppConfig[] = [
  {
    id: 'agent-studio-lite',
    category: AppCategory.TEST,
    basePath: '/agent-studio-lite',
    displayName: 'Agent Studio Lite',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: () => 'http://localhost:3002/index.js',
      },
    ],
    moduleConfig: (pspContext) => {
      const { hostingEnvironment } = pspContext;
      const ogHostingDomain = getHostingEnvironmentDomain();
      return {
        env: hostingEnvironment,
        agentsApiBaseUrl: `https://ai.${ogHostingDomain}`,
        platformApiBaseUrl: `https://controlpanel.${ogHostingDomain}`,
      };
    },
    icon: 'Xml',
    description: 'React 19 test application with task management',
    menuSection: MenuSectionTitle.TEST_APPS,
  },
  {
    id: 'external-url-new-tab',
    category: AppCategory.TEST,
    basePath: '/external-url-new-tab',
    displayName: 'URL [New Tab]',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    externalUrl: () => {
      return 'https://www.google.com';
    },
    openInNewTab: true,
    icon: 'Xml',
    menuSection: MenuSectionTitle.TEST_APPS,
  },
  {
    id: 'external-url-iframe',
    category: AppCategory.TEST,
    basePath: '/external-url-iframe',
    displayName: 'URL [Iframe]',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    externalUrl: () => {
      return 'https://www.example.com';
    },
    openInNewTab: false,
    icon: 'Xml',
    menuSection: MenuSectionTitle.TEST_APPS,
  },
  {
    id: 'finance-dashboard',
    category: AppCategory.TEST,
    basePath: '/finance-dashboard',
    displayName: 'Finance Dashboard',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleConfig: (pspContext) => {
      return {
        activeEntity: pspContext.activeEntity,
        baseApiUrl: (() => {
          switch (pspContext.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return 'http://localhost:3001';
            case HostingEnvironment.PRODUCTION:
              return 'https://cdn.opengov.com';
            default:
              return '';
          }
          return '';
        })(),
      };
    },
    moduleRoutes: [
      {
        pattern: '/',
        resolver: () => {
          return 'http://localhost:3001/index.js';
        },
      },
      {
        pattern: '/reports',
        resolver: () => {
          return 'http://localhost:3001/index.js';
        },
      },
      {
        pattern: '/settings',
        resolver: () => {
          return 'http://localhost:3001/index.js';
        },
      },
    ],
    icon: 'Xml',
    description: 'React 18 test app (consumes React via PSP import-map singleton)',
    menuSection: MenuSectionTitle.TEST_APPS,
  },
] as const satisfies readonly TestAppConfig[];

// --- Other apps (not in the four main groups) ---
const adminSettingsConfig: EntitledAppConfig = {
  id: EntityApplicationProductKey.ADMIN_SETTINGS,
  category: AppCategory.ENTITLED,
  basePath: '/entity',
  displayName: 'Admin Settings',
  triggerPageRefreshOnNavigateAway: (ctx) =>
    !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
  moduleRoutes: [
    {
      pattern: '/',
      resolver: () => adminSettingsModuleUrl('/entity/assets/entity-psp.js'),
    },
  ],
  // TODO: Replace placeholder URN with real subject pattern once Admin Settings adopts Beacon notifications
  resourceSubjectPatterns: ['urn:opengov:adminsettings'],
  icon: 'AdminSettings',
  description: 'Centrally manage your entity data including groups, users, and roles',
  menuSection: MenuSectionTitle.PREFERENCES,
};

// Chronos audit-log micro-frontend, shown in the Preferences section to any user
// holding a scoped audit READ or READ_PII claim at the active entity
// (PLATFORM_AUDIT__{READ|READ_PII}__{product}[__{service}[__{targetType}]]),
// independent of entitlements. Matches Chronos, where READ_PII includes READ.
// Chronos itself scopes the rows to those claims and returns 403 without one,
// so a direct link to /audit without a claim shows its forbidden page.
const chronosAuditLogConfig: StaticAppConfig = {
  id: 'chronos-audit-logs',
  category: AppCategory.STATIC,
  requiredClaimPrefixes: ['PLATFORM_AUDIT__READ__', 'PLATFORM_AUDIT__READ_PII__'],
  basePath: '/audit',
  displayName: 'Audit Logs',
  triggerPageRefreshOnNavigateAway: (ctx) =>
    !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
  moduleRoutes: [
    {
      pattern: '/',
      resolver: () => {
        // FOR LOCAL TESTING against a locally-served chronos PSP bundle:
        //return 'https://localhost:5174/index.js';
        const ogHostingDomain = getHostingEnvironmentDomain();
        return `https://chronos.${ogHostingDomain}/psp/index.js`;
      },
    },
  ],
  resourceSubjectPatterns: ['urn:opengov:chronos:audit'],
  icon: 'AuditLog',
  description: 'View audit log events for this entity',
  menuSection: MenuSectionTitle.PREFERENCES,
  featureFlagKey: FeatureFlagKey.PSP_UI_AUDIT_LOGS,
};

const preferences: readonly (EntitledAppConfig | StaticAppConfig)[] = [
  adminSettingsConfig,
  chronosAuditLogConfig,
];

// App Builder (product key `gab`) migrates from the legacy v1 iframe
// (`gab-frontend`) to the gab-core v2 PSP ES module. It uses the same
// dual-topology pattern as PLC above: a single entry carries BOTH
// `moduleRoutes` and `externalUrl`, and `appBuilderMountsInShell(ctx)` picks
// which is live per environment/flag. This is safe because `PspContext`
// resolves both resolvers to concrete strings before `isIFrameApp`/
// `isRemoteApp` run — an `externalUrl` that resolves to '' is treated as
// "not external", so the entry falls through to `RemoteAppShell` (the v2
// module); a non-empty `externalUrl` (flag off) keeps the v1 iframe. See
// PSS-2932.
//
// Every environment (including dev) mounts v2 for this entry only when the
// per-community `psp-enable-app-builder-v2` flag is on, otherwise falls back
// to the legacy v1 iframe — one rule, no per-environment special-casing. The
// apps built in gab-core are separate: the `/a` route registry entry mounts
// the same v2 module for them under `psp-ui-dynamic-gab-apps` alone, because
// those apps exist only in v2 whichever way this flag is set. Locally, use the
// LaunchDarkly Developer Toolbar to flip the flag for testing (see CLAUDE.md);
// `VITE_DEV_MODULE_URL_APP_BUILDER` still applies once the flag is on (see
// `gabCoreModuleUrl`'s DEVELOPMENT case — unrelated to this gate).
// Unlike PLC, `openInNewTab` is intentionally NOT set, so the flag-off path
// stays an in-shell iframe (v1's current behavior), not a new-tab redirect.
const appBuilderMountsInShell = (ctx: AppRegistryCallbackContext): boolean =>
  !!ctx.featureFlags?.[FeatureFlagKey.PSP_ENABLE_APP_BUILDER_V2];

// Legacy v1 (gab-frontend) iframe URL — the target when NOT mounting v2
// in-shell. Uses the same trusted origin as dynamic v1 app entries.
const appBuilderV1IframeUrl = (): string => `${gabV1Origin()}/apps`;

const capabilities: readonly EntitledAppConfig[] = [
  {
    id: EntityApplicationProductKey.AGENT_STUDIO,
    category: AppCategory.ENTITLED,
    basePath: '/agents',
    displayName: 'Agent Studio',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: () => {
          const ogHostingDomain = getHostingEnvironmentDomain();
          return `https://ai.${ogHostingDomain}/psp-module/index.js`;
        },
      },
    ],
    moduleConfig: (pspContext) => {
      return {
        env: pspContext.hostingEnvironment,
      };
    },
    // TODO: Replace placeholder URN with real subject pattern once Agent Studio adopts Beacon notifications
    resourceSubjectPatterns: ['urn:opengov:agentstudio'],
    icon: 'AgentStudio',
    description: 'Accelerate internal workflows with AI-powered assistance',
    menuSection: MenuSectionTitle.CAPABILITIES,
  },
  {
    id: EntityApplicationProductKey.APP_BUILDER,
    category: AppCategory.ENTITLED,
    basePath: '/app-builder',
    displayName: 'App Builder',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    // v2 in-shell: `moduleRoutes` resolves to the gab-core module URL and
    // `externalUrl` resolves to '' (so isIFrameApp/isNewTabApp ignore it,
    // and the entry falls through to RemoteAppShell). Flag off: `moduleRoutes`
    // resolves to '' and `externalUrl` points at the v1 iframe app. See
    // appBuilderMountsInShell / PSS-2932.
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => (appBuilderMountsInShell(ctx) ? gabCoreModuleUrl(ctx) : ''),
      },
    ],
    externalUrl: (ctx) => (appBuilderMountsInShell(ctx) ? '' : appBuilderV1IframeUrl()),
    // Base GAB namespace. gab-core stamps every notification it sends with this base, optionally
    // extended by an admin-entered sub-namespace. Keep this as the catch-all: more specific
    // patterns (e.g. `urn:opengov:gab:311` on PRODUCT_311) win on specificity, and anything
    // without a registered sub-namespace falls back here.
    resourceSubjectPatterns: ['urn:opengov:gab'],
    icon: 'AppBuilder',
    description: 'App Builder',
    menuSection: MenuSectionTitle.CAPABILITIES,
  },
  {
    id: EntityApplicationProductKey.WORKFLOW_BUILDER,
    category: AppCategory.ENTITLED,
    basePath: '/workflows',
    displayName: 'Workflow Builder',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const integrationUrl = 'https://workflows.bifrost.ogintegration.us/psp/index.js';
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return resolveDevModuleUrl('WORKFLOW_BUILDER', integrationUrl);
            case HostingEnvironment.INTEGRATION:
              return integrationUrl;
            case HostingEnvironment.STAGING:
              return 'https://workflows.bifrost.ogstaging.us/psp/index.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://workflows.bifrost.opengov.com/psp/index.js';
            default:
              return '';
          }
        },
      },
    ],
    resourceSubjectPatterns: ['urn:opengov:wfb'],
    icon: 'WorkflowBuilder',
    description:
      'Build, manage, and operate workflows with health monitoring and lifecycle controls',
    menuSection: MenuSectionTitle.CAPABILITIES,
  },
  {
    id: EntityApplicationProductKey.ATLAS,
    category: AppCategory.ENTITLED,
    basePath: '/atlas',
    displayName: 'Reference Data Hub',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    resourceSubjectPatterns: ['urn:opengov:atlas'],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const integrationUrl = 'https://atlas.ogintegration.us/psp/atlas.js';
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return resolveDevModuleUrl('ATLAS', integrationUrl);
            case HostingEnvironment.INTEGRATION:
              return integrationUrl;
            case HostingEnvironment.STAGING:
              return 'https://atlas.ogstaging.us/psp/atlas.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://atlas.opengov.com/psp/atlas.js';
            default:
              return integrationUrl;
          }
        },
      },
    ],
    icon: 'DataManager',
    description: 'Master data management for government entities',
    menuSection: MenuSectionTitle.CAPABILITIES,
  },
  {
    id: EntityApplicationProductKey.VENDOR_MANAGEMENT,
    category: AppCategory.ENTITLED,
    basePath: '/vendor-management',
    displayName: 'Vendor Management',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    resourceSubjectPatterns: ['urn:opengov:vm:vendor'],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const integrationUrl =
            'https://vendor.procurement.ogintegration.us/psp/vendor-management.js';
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return resolveDevModuleUrl('VENDOR_MANAGEMENT', integrationUrl);
            case HostingEnvironment.INTEGRATION:
              return integrationUrl;
            case HostingEnvironment.STAGING:
              return 'https://vendor.ogstaging.us/psp/vendor-management.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://vendor.opengov.com/psp/vendor-management.js';
            default:
              return integrationUrl;
          }
        },
      },
    ],
    icon: 'VendorManagement',
    description: 'Vendor management for government entities',
    menuSection: MenuSectionTitle.CAPABILITIES,
  },
  {
    id: EntityApplicationProductKey.CLM,
    category: AppCategory.ENTITLED,
    basePath: '/contract-management',
    displayName: 'Contract Lifecycle Management',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    resourceSubjectPatterns: ['urn:opengov:clm:contract'],
    moduleConfig: () => ({
      taskmaster: { baseUrl: getTaskmasterOrigin() },
    }),
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const integrationUrl =
            'https://contract-management.procurement.ogintegration.us/psp/index.js';
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return resolveDevModuleUrl('CLM', integrationUrl);
            case HostingEnvironment.INTEGRATION:
              return integrationUrl;
            case HostingEnvironment.STAGING:
              return 'https://contract-management.procurement.ogstaging.us/psp/index.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://contract-management.procurement.opengov.com/psp/index.js';
            default:
              return integrationUrl;
          }
        },
      },
    ],
    icon: 'ContractManagement',
    description: 'Manage contracts through execution and renewal',
    menuSection: MenuSectionTitle.CAPABILITIES,
  },
  {
    id: EntityApplicationProductKey.BUDGET,
    category: AppCategory.ENTITLED,
    basePath: '/stories',
    displayName: 'Publications',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const integrationUrl =
            'https://d31s9l7p2d7cou.cloudfront.net/psp/assets/psp/stories-psp.js';
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return resolveDevModuleUrl('STORIES', integrationUrl);
            case HostingEnvironment.INTEGRATION:
              return integrationUrl;
            case HostingEnvironment.STAGING:
              return 'https://d3ficuh9prwktj.cloudfront.net/psp/assets/psp/stories-psp.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://d1xpcacohn4ion.cloudfront.net/psp/assets/psp/stories-psp.js';
            default: {
              console.error(
                `Stories PSP module: unhandled hosting environment ${ctx.hostingEnvironment}. ` +
                  `Showcase URL needs to be added once that env has been deployed.`
              );
              return '';
            }
          }
        },
      },
    ],
    resourceSubjectPatterns: ['urn:opengov:stories'],
    icon: 'GeneralLedger',
    description: 'OpenGov Publications',
    menuSection: MenuSectionTitle.CAPABILITIES,
  },
  {
    id: EntityApplicationProductKey.SITES,
    category: AppCategory.ENTITLED,
    basePath: '/sites',
    featureFlagKey: FeatureFlagKey.PSP_UI_SITES,
    displayName: 'Sites',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    moduleConfig: () => ({
      apiBaseUrl: getBlueprintApiBaseUrl(),
      // Blueprint Phase 4: embed @opengov/taskmaster-actions on pending_review pages
      taskmasterBaseUrl: getTaskmasterOrigin(),
      platformBaseUrl: getPspOrigin(),
    }),
    moduleRoutes: [
      {
        pattern: '/',
        resolver: async (ctx) => {
          const integrationUrl = await resolveBlueprintModuleUrl();
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return resolveDevModuleUrl('SITES', integrationUrl);
            case HostingEnvironment.INTEGRATION:
            case HostingEnvironment.STAGING:
            case HostingEnvironment.PRODUCTION:
              return integrationUrl;
            default:
              return integrationUrl;
          }
        },
      },
    ],
    resourceSubjectPatterns: ['urn:opengov:sites'],
    icon: 'Xml',
    description: 'Blueprint CMS for government websites — create, edit, and publish public sites',
    menuSection: MenuSectionTitle.CAPABILITIES,
  },
] as const satisfies readonly EntitledAppConfig[];

// --- Apps Under Construction: Apps hidden in Production until ready for demo ---
const appsUnderConstruction: readonly EntitledAppConfig[] = [
  {
    id: EntityApplicationProductKey.OD,
    category: AppCategory.ENTITLED,
    basePath: '/open-data',
    featureFlagKey: FeatureFlagKey.PSP_UI_APPS_UNDER_CONSTRUCTION,
    displayName: 'Open Data',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    externalUrl: (pspContext: AppRegistryCallbackContext) => {
      switch (pspContext.hostingEnvironment) {
        case HostingEnvironment.DEVELOPMENT:
          return 'https://ckansandbox.ogopendata.com';
        case HostingEnvironment.INTEGRATION:
          return 'https://ckansandbox.ogopendata.com';
        case HostingEnvironment.PRODUCTION:
          return 'https://cloudcity.ogopendata.com';
        default:
          return 'https://ckansandbox.ogopendata.com';
      }
    },
    openInNewTab: false,
    icon: 'OpenData',
    description: 'Open Data portal',
    menuSection: MenuSectionTitle.CAPABILITIES,
  },
] as const satisfies readonly EntitledAppConfig[];

// --- Dev-only Action Hubs: Apps shown only in DEV mode but placed in ACTION_HUBS section ---
const notifications: readonly StaticAppConfig[] = [
  {
    id: 'tasks-and-notifications',
    featureFlagKey: FeatureFlagKey.PSP_UI_NOTIFICATIONS,
    category: AppCategory.STATIC,
    basePath: '/tasks-and-notifications',
    displayName: 'Tasks and Notifications',
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    component: NotificationsPage,
    icon: 'Notifications',
    description: 'Tasks and notifications',
    menuSection: MenuSectionTitle.ACTION_HUBS,
  },
] as const satisfies readonly StaticAppConfig[];

// Default app registry — combines all app types.
// Per-app gating is declared via `featureFlagKey` on each entry; PspContextProvider
// filters this unresolved list against LaunchDarkly at runtime.
export const appRegistry: readonly UnresolvedAppConfig[] = [
  // Test apps are dev-only — Vite statically replaces import.meta.env.DEV at build time,
  // so the entire testApps array (and its lazy modules) is tree-shaken from production builds.
  // The `typeof` guard keeps this safe outside Vite (e.g. Playwright e2e helpers that import
  // this file via the standard TS loader where `import.meta.env` is undefined).
  ...(typeof import.meta.env !== 'undefined' && import.meta.env.DEV ? testApps : []),
  ...products,
  ...actionHubs,
  pspDemoAssistantConfig,
  governmentOsConfig,
  ogIntelligenceConfig,
  og360Config,
  ...notifications,
  ...preferences,
  ...capabilities,
  ...appsUnderConstruction,
  ...devTools,
] as const satisfies readonly UnresolvedAppConfig[];
